/**
 * Seal / open primitives for the encrypted-local-backup file (web).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BackupCipher.swift` in shape; the web
 * baseline uses PBKDF2-SHA256 600 000 iterations because that is the only
 * passphrase-KDF that `crypto.subtle` ships natively. The on-disk algorithm
 * field is a discriminator so a future Argon2id-via-WASM swap can coexist
 * with already-generated `pbkdf2-sha256-600k` files.
 *
 * THREAT MODEL
 *   - File at rest is untrusted: an attacker may flip bytes anywhere
 *     (header or ciphertext). Defense: AES-GCM with the canonical-encoded
 *     header as AAD — any tamper surfaces as `BackupError.authFailed`.
 *   - Resource exhaustion: a hostile file claiming a multi-GB ciphertext
 *     would OOM the device. Defense: `MAX_PLAINTEXT_BYTES` cap; the check
 *     fires before the AES-GCM decrypt is invoked.
 *   - Same-passphrase replay: identical plaintext + same passphrase on
 *     two days must NOT produce identical ciphertexts. Defense: every
 *     `seal` generates a fresh 32-byte salt + 12-byte nonce via
 *     `crypto.getRandomValues`.
 *   - Wrong-passphrase oracle: an attacker should not be able to tell
 *     "wrong passphrase" from "tampered ciphertext" from the API surface.
 *     Defense: every AEAD-failure path collapses into a single
 *     `authFailed` error kind.
 *
 * Service-Worker importability guarantee:
 *   Uses ONLY `crypto.subtle`, `crypto.getRandomValues`, `TextEncoder`,
 *   `Uint8Array`, and `btoa`/`atob` (all available in SW + main thread).
 *   No `window`, no `document`, no DOM-only APIs.
 */

import {
  BACKUP_MAGIC,
  BACKUP_NONCE_BYTES,
  BACKUP_SALT_BYTES,
  BACKUP_TAG_BYTES,
  BackupError,
  canonicalHeaderBytes,
  encodeFileBytes,
  parseFileBytes,
  type BackupHeader,
  type BackupKdfAlgorithm,
} from "./BackupHeader";

/**
 * 100 MB cap. Bounds memory for `open()` and prevents a malicious file
 * from triggering OOM. Enforced BEFORE the AES-GCM decrypt is invoked
 * (so a hostile file claiming a 10GB ciphertext is rejected without
 * allocating the output buffer). Mirrors iOS `maxPlaintextBytes`.
 */
export const MAX_PLAINTEXT_BYTES = 100 * 1024 * 1024;

/** PBKDF2 iteration count for the web baseline. See file header. */
const PBKDF2_ITERATIONS = 600_000;

/** AES-GCM key length in bytes. */
const AES_KEY_BYTES = 32;

/**
 * Seal arbitrary plaintext into a kaether-backup-v1 file. Generates a fresh
 * 32-byte salt + 12-byte nonce on every call so the same input + same
 * passphrase produces a different file every time.
 *
 * @throws BackupError if `plaintext.length > MAX_PLAINTEXT_BYTES` or the
 *         passphrase fails the policy gate. Crypto-level failures (which
 *         require a broken-RNG / broken-WebCrypto host) surface as
 *         `authFailed`.
 *
 * @param passphrase  UTF-8 string. Caller should pre-validate via
 *                    `validatePassphrase(...)` for instant UI feedback;
 *                    this function does NOT re-validate (the policy
 *                    validator is the single source of truth and the
 *                    cipher accepts any non-empty bytes).
 * @param plaintext   Opaque bytes to seal. Bundle layer (BackupBundle.ts)
 *                    is responsible for the JSON shape; this layer is
 *                    bytes-in, bytes-out.
 * @param createdAt   Unix seconds. Defaults to "now". Pass an explicit
 *                    value when reproducibility matters (tests).
 */
export async function seal(
  passphrase: string,
  plaintext: Uint8Array,
  createdAt: number = Math.floor(Date.now() / 1000),
): Promise<Uint8Array> {
  if (plaintext.length > MAX_PLAINTEXT_BYTES) {
    throw new BackupError("payloadOverLimit", plaintext.length);
  }
  const kdfAlgorithm: BackupKdfAlgorithm = "pbkdf2-sha256-600k";
  const salt = randomBytes(BACKUP_SALT_BYTES);
  const nonce = randomBytes(BACKUP_NONCE_BYTES);
  const header: BackupHeader = {
    magic: BACKUP_MAGIC,
    kdfAlgorithm,
    kdfSalt: salt,
    cipherNonce: nonce,
    createdAt,
  };
  const aad = canonicalHeaderBytes(header);
  const key = await deriveKey(passphrase, salt, kdfAlgorithm);
  let combined: ArrayBuffer;
  try {
    combined = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, additionalData: aad },
      key,
      plaintext,
    );
  } catch (e) {
    throw new BackupError("authFailed", "encrypt failure");
  }
  // SubtleCrypto returns ciphertext || tag in one buffer; split into the
  // wire-format fields the bundle JSON shape expects.
  const combinedU8 = new Uint8Array(combined);
  if (combinedU8.length < BACKUP_TAG_BYTES) {
    throw new BackupError("authFailed", "short combined output");
  }
  const ciphertextLen = combinedU8.length - BACKUP_TAG_BYTES;
  const ciphertext = combinedU8.subarray(0, ciphertextLen);
  const authTag = combinedU8.subarray(ciphertextLen);
  return encodeFileBytes(header, ciphertext, authTag);
}

/**
 * Parse + decrypt a kaether-backup-v1 file. Returns the original plaintext
 * on success. AES-GCM AAD verification covers the header, so any mutation
 * of `kdfSalt`, `cipherNonce`, `kdfAlgorithm`, or `createdAt` surfaces as
 * `authFailed`.
 *
 * The 100MB cap is enforced BEFORE the AES-GCM decrypt is invoked so a
 * hostile file claiming a 10GB ciphertext is rejected without allocating
 * the output buffer.
 *
 * @param passphrase  UTF-8 string the user supplied.
 * @param sealed      File bytes produced by a prior `seal(...)` call.
 *                    Total file size > 100MB is rejected without parsing.
 */
export async function open(
  passphrase: string,
  sealed: Uint8Array,
): Promise<Uint8Array> {
  // DoS gate: check the outer file-size BEFORE doing any work. A hostile
  // 10GB file is rejected here without touching the JSON parser.
  if (sealed.length > MAX_PLAINTEXT_BYTES) {
    throw new BackupError("payloadOverLimit", sealed.length);
  }
  const bundle = parseFileBytes(sealed);
  if (bundle.header.magic !== BACKUP_MAGIC) {
    throw new BackupError("unsupportedMagic");
  }
  if (bundle.header.kdfSalt.length !== BACKUP_SALT_BYTES) {
    throw new BackupError("invalidSaltLength", bundle.header.kdfSalt.length);
  }
  if (bundle.header.cipherNonce.length !== BACKUP_NONCE_BYTES) {
    throw new BackupError("invalidNonceLength", bundle.header.cipherNonce.length);
  }
  if (bundle.authTag.length !== BACKUP_TAG_BYTES) {
    throw new BackupError("authFailed", "tag length");
  }
  if (bundle.ciphertext.length > MAX_PLAINTEXT_BYTES) {
    throw new BackupError("payloadOverLimit", bundle.ciphertext.length);
  }
  const aad = canonicalHeaderBytes(bundle.header);
  const key = await deriveKey(
    passphrase,
    bundle.header.kdfSalt,
    bundle.header.kdfAlgorithm,
  );
  // SubtleCrypto AES-GCM open expects ciphertext || tag in a single buffer.
  const combined = new Uint8Array(bundle.ciphertext.length + bundle.authTag.length);
  combined.set(bundle.ciphertext, 0);
  combined.set(bundle.authTag, bundle.ciphertext.length);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: bundle.header.cipherNonce, additionalData: aad },
      key,
      combined,
    );
  } catch (e) {
    // AES-GCM auth failure collapses into a single error kind regardless
    // of cause: wrong passphrase, tampered header, tampered ciphertext,
    // or wrong tag all look the same to the API caller (and to a side-
    // channel observer). The UI surfaces one piece of copy.
    throw new BackupError("authFailed");
  }
  return new Uint8Array(plaintext);
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

/**
 * Derive the AES-GCM key from a passphrase. Dispatches on `algorithm` so
 * a future Argon2id (via WASM) case can be added without changing call
 * sites.
 */
async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  algorithm: BackupKdfAlgorithm,
): Promise<CryptoKey> {
  switch (algorithm) {
    case "pbkdf2-sha256-600k":
      return derivePbkdf2(passphrase, salt, PBKDF2_ITERATIONS);
    default: {
      // Exhaustiveness check — TypeScript narrows `algorithm` to `never`
      // here. Adding a new union variant makes this branch a compile error.
      const _exhaustive: never = algorithm;
      throw new BackupError("unsupportedKdfAlgorithm", String(_exhaustive));
    }
  }
}

async function derivePbkdf2(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const passBytes = new TextEncoder().encode(passphrase);
  const baseKey = await crypto.subtle.importKey(
    "raw",
    passBytes,
    "PBKDF2",
    /* extractable = */ false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations,
    },
    baseKey,
    { name: "AES-GCM", length: AES_KEY_BYTES * 8 },
    /* extractable = */ false,
    ["encrypt", "decrypt"],
  );
}

/** Cryptographically-random byte generator via `crypto.getRandomValues`. */
function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return buf;
}
